/* Component for ShippingReturns section in the PDP */

import { useState, useEffect } from "react";
import { useRouter } from "next/compat/router";
import Link from "next/link";

import styles from "../sections.module.scss";
import { generalConstants } from "@/utils/constants";
import Srcset from "@/components/common/Srcset";
import EstimatedDelivery from "@/components/common/EstimatedDelivery";
import { setGroupShipDate } from "@/helpers/checkout-shipping/checkoutShippingHelper";

const ShippingReturns = ({ showTitle = true, isFinalSale, christmasMsg,tags, estimatedDeliveryDate, brand, isExchangeOnly }) => {
  let country = 1;

  // For Testing
  const router = useRouter();
  if (router.query.country) {
    if (router.query.country !== "1") {
      country = 2;
    }
  }
  let expressShippingdate;
  let standardShippingdate;
  let internationalShippingdate;

  //OS-3693: Dynamic shipping options
  try {
    //calculation of estimated delivery date for standard, international and express shipping
    if (tags) {
      //if tag includes international then product is eligible for international shipping
      if (tags.includes("International")) {
        internationalShippingdate = setGroupShipDate(
          { product: { tags } },
          { min: 6, max: 18 }
        ); //International Shipping
      }
      //if tag includes group-0 then product is eligible for express shipping
      if (tags.includes("group-0")) {
        expressShippingdate = setGroupShipDate(
          { product: { tags } },
          { min: 1, max: 2 }
        ); //Express Shipping
      }
      // all products are eligible for standard shipping
      standardShippingdate = setGroupShipDate({ product: { tags } }); //Standard Shipping
    }
  } catch (error) {
    console.log(error);
  }

  //create returns text
  let returnsText = "";
  if (isFinalSale)
    returnsText =
      "This item is final sale and cannot be returned. ";
  else if (isExchangeOnly)
    returnsText = 
      "This item is size exchange only and cannot be returned. You have 7 days to request an exchange for an alternative size. ";    
  else {
    returnsText=`We hope you like your purchase but if not, you have 30 days to request a return or free exchange. `
  }

  // Shipping type message
  let shippingMessage = "";
  if(!tags?.includes("group-0") && !tags?.includes("International")){
    shippingMessage = "This product is not available for express or international shipping";
  }else if(!tags?.includes("group-0")){
    shippingMessage = "This product is not available for express shipping";
  }else if(!tags?.includes("International")){
    shippingMessage = "This product is not available for international shipping";
  }

  return (
    <>
      <div className={styles["main-drawer"]}>
        {/* Shipping Section */}
        <div className={`${styles.shipping_info}`}>
          {showTitle && <div className={styles.sub_block}>
            <h5 className={`light-md x-large-md large-sm ${styles.heading}`} id="productDescHeading-shipping">
              Shipping
            </h5>
          </div>}
          {christmasMsg ? 
            <div className={styles.sub_block}>
              <div className={`${styles.christmas_wrapper}`}>
                <Srcset
                  requirePath={true}
                  svgFile={true}
                  src={"images/Icons/snowflake.svg"}
                  alt="Snowflake"
                  height={19}
                  width={21}
                  className={`${styles.christmas_image}`}
                /> 
                <div className={`x-small ${styles.christmas_text}`} >
                  Order now to receive before December 24th
                </div> 
              </div>
            </div> : ""
          }
          {/* Standard Shipping */}
          <div className={styles.sub_block}>
            <div className="row">
              <div className={`grid-xs-9 `}>
                <p className={`medium x-small-xs small-sm normal-md ${styles.shipping_type}`}>Standard shipping</p>
              </div>
              <div className={`grid-xs-3 text-end `}>
                <p className={`medium x-small-xs small-sm normal-md ${styles.shipping_type}`}>Free</p>
              </div>
            </div>
            <EstimatedDelivery deliveryDate={standardShippingdate?.property?.value} estimatedDeliveryDate={estimatedDeliveryDate} />
          </div>
            {/* Express Shipping */}
            {tags?.includes("group-0") && (
            <div className={styles.sub_block}>
              <div className="row">
                <div className={`grid-xs-9 `}>
                  <p className={`medium x-small-xs small-sm normal-md ${styles.shipping_type}`}>Express shipping</p>
                </div>
                <div className={`grid-xs-3 text-end `}>
                  <p className={`medium x-small-xs small-sm normal-md ${styles.shipping_type}`}>$15</p>
                </div>
              </div>
              <EstimatedDelivery deliveryDate={expressShippingdate?.property?.value}/>
            </div>
            )}
            {/* International Shipping */}
            {tags?.includes("International") && (
            <div className={styles.sub_block}>
              <div className="row">
                <div className={`grid-xs-9 `}>
                  <p className={`medium x-small-xs small-sm normal-md ${styles.shipping_type}`}>International shipping</p>
                  {/* <div className={`x-small-xs small-sm small-md ${styles.estimated_date}`}>Estimated ship date: DD Month</div> */}
                </div>
                <div className={`grid-xs-3 text-end `}>
                  <p className={`medium x-small-xs small-sm normal-md ${styles.shipping_type}`}>$30</p>
                </div>
              </div>
              <EstimatedDelivery deliveryDate={internationalShippingdate?.property?.value}/>
            </div>
            )}
            {shippingMessage ? (
              <div className={`x-small-md x-small-sm ${styles.deliveryText} ${styles.sub_block} italic`} id="productDescription-shippingMessage">
                {shippingMessage}
              </div>
            ) : null}

            {/* Returns section */}
          <div className={`${styles.sub_block} returns_sub_block `}>
            <h5 className={`light-md x-large-md large-sm small-xs ${styles.heading}`} id="productDescHeading-returns">Returns</h5>
            <div className={`pwa-link-tag x-small-xs small-sm normal-md ${styles.sub_text}`} id="productDesc-returns">
            {returnsText}
            <>See <a href="https://help.olivela.com/en_us/categories/returns-exchanges-Sk3yalm70" aria-label="View faqs" tabIndex={0}>here</a> for more details.</>
            </div>
          </div>

          {/* Shipping Vendor Section */}
          {tags?.includes("Dropship") && (
            <div className={`${styles.sub_block} shipping_vendor_sub_block `}>
              <h5 className={`light-md x-large-md large-sm small-xs bold ${styles.heading} ${brand?.logo && styles.shipping_vendor_heading}`} id="productCareHeading-shippingVendor">
                Shipping from {brand?.logo ? (
                  <Srcset
                    requirePath={false}
                    svgFile={false}
                    src={brand?.logo}
                    alt={`${brand?.brand} Logo"`}
                    className={`${styles.brand_logo}`}
                  />
              ) : (<span className={`${styles.brand_name}`}>{brand?.brand}</span>)}</h5>
              <div className={`x-small-xs small-sm normal-md ${styles.sub_text}`} id="productCareDesc">
                We collaborate with our designers to support our mission to give back and to reduce our carbon footprint. This item will ship to you directly from {brand?.brand}'s location and may arrive in their branded packaging.
              </div>
            </div>
          )} 
          <div className={styles.sub_block}>
            <div className={`x-small-xs small-sm normal-md ${styles.sub_text}`} id="productDescription-contact">
              Need assistance? Our dedicated customer care team is happy to help. <Link href={`mailto:${generalConstants.help_email}`} className="ga-help" aria-label="Contact us" tabIndex={0}>Contact us</Link> by email—we’re available Monday to Friday, 8 AM–4 PM EST.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShippingReturns;
